import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Calendar, FileText, MessageSquare, TrendingUp,
  Clock, ChevronDown, ChevronUp, ArrowLeft,
  Mail, Phone, Building, AlertCircle, Brain, DollarSign, ListTodo
} from 'lucide-react';
import { clientService, type Client } from '../../services/clientService';
import type { Note } from '../../types/notes';  // TASK-930: notes on the timeline
import kycService from '../../services/kycService';
import { useClientDna, summarizeDna } from '../../hooks/useClientDna';
import {
  calculateTotalAssets,
  calculateTotalLiabilities,
  calculateTotalIncome,
  calculateNetWorth,
} from '../risk-profile/schemas/riskProfileSchema';
import DocumentsTab from './tabs/DocumentsTab';
import NotesTab from './tabs/NotesTab';
import ClientInsightsTab from './tabs/InsightsTab';
import PortfoliosTab from './tabs/PortfoliosTab';
import DnaCard from '../dna/DnaCard';
import VoiceNoteWidget from '../dna/VoiceNoteWidget';
import { MeetingActionItemsWidget } from '../canvas/widgets';
// TASK-229: ComplianceTab replaces KYCTab and RiskProfileTab
import { ComplianceTab } from '../compliance';
// TASK-210: Import seed data
import { getSeedClient } from '../../services/mock/comprehensiveClientSeedData';
// TASK-358: Task modal
import AddTaskModal from '../tasks/AddTaskModal';
import { taskService } from '../../services/taskService';
import { riskProfileStatusLabel } from '../../utils/statusTaxonomy';
import { TaskCreateRequest } from '../../types/tasks';
import { toast } from 'sonner';

interface TimelineEvent {
  id: string;
  date: string;
  type: 'meeting' | 'life_event' | 'communication' | 'document';
  title: string;
  description: string;
  metadata?: any;
  icon: React.ReactNode;
  color: string;
}

const ClientDetail: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  // TASK-604: the snapshot header hydrates from the knowledge graph (same fetch
  // the DNA tab uses — react-query dedupes them to one request).
  const { data: dnaData } = useClientDna(clientId ?? '');
  const dnaSummary = useMemo(() => summarizeDna(dnaData), [dnaData]);
  // TASK-229: Replace KYC and Risk Profile tabs with unified Compliance tab
  const [activeTab, setActiveTab] = useState<'timeline' | 'documents' | 'compliance' | 'notes' | 'insights' | 'portfolios' | 'dna' | 'voice' | 'actions'>('timeline');
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  // TASK-654: the timeline is grouped by month (older months collapsed by
  // default) and filterable by event type — no default wall of records.
  const [timelineFilter, setTimelineFilter] = useState<
    'all' | 'meeting' | 'life_event' | 'communication' | 'document'
  >('all');
  const [expandedMonths, setExpandedMonths] = useState<Set<string> | null>(null);

  const groupedTimeline = useMemo(() => {
    const filtered =
      timelineFilter === 'all'
        ? timelineEvents
        : timelineEvents.filter((e) => e.type === timelineFilter);
    const groups = new Map<string, TimelineEvent[]>();
    for (const e of filtered) {
      const d = new Date(e.date);
      const key = Number.isNaN(d.getTime())
        ? 'undated'
        : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(e);
    }
    const keys = [...groups.keys()].sort().reverse();
    const defaultOpen = new Set(keys.slice(0, 3)); // recent months stay open
    return keys.map((month) => ({
      month,
      label:
        month === 'undated'
          ? 'Undated'
          : new Date(`${month}-01T00:00:00`).toLocaleDateString(undefined, {
              month: 'long',
              year: 'numeric',
            }),
      events: groups.get(month)!,
      collapsed: expandedMonths ? !expandedMonths.has(month) : !defaultOpen.has(month),
    }));
  }, [timelineEvents, timelineFilter, expandedMonths]);

  const toggleMonth = (month: string) =>
    setExpandedMonths((prev) => {
      const base = prev ?? new Set(groupedTimeline.filter((g) => !g.collapsed).map((g) => g.month));
      const next = new Set(base);
      if (next.has(month)) next.delete(month);
      else next.add(month);
      return next;
    });

  // KYC profile data (personal info, financial snapshot)
  const [kycProfile, setKycProfile] = useState<any>(null);

  // TASK-358: Task modal state
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isCreatingTask, setIsCreatingTask] = useState(false);

  // Handle initial tab from URL query parameter
  // TASK-229: Updated tab list and legacy URL redirects
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    // TASK-604: dna + voice are deep-linkable (alert group cards navigate ?tab=dna).
    const validTabs = ['timeline', 'documents', 'compliance', 'notes', 'insights', 'portfolios', 'dna', 'voice', 'actions'];

    // Handle legacy URLs: redirect kyc/risk-profile to compliance with subtab
    if (tabParam === 'kyc' || tabParam === 'risk-profile') {
      setActiveTab('compliance');
      return;
    }

    if (tabParam && validTabs.includes(tabParam)) {
      setActiveTab(tabParam as 'timeline' | 'documents' | 'compliance' | 'notes' | 'insights' | 'portfolios' | 'dna' | 'voice' | 'actions');
    }
  }, [searchParams]);

  useEffect(() => {
    if (clientId) {
      fetchClientDetails();
    }
  }, [clientId]);

  const fetchClientDetails = async () => {
    if (!clientId) return;

    setLoading(true);
    try {
      let enrichedClient: Client | null = null;

      // TASK-555: real API is the source of truth. Seed substitution is DEV-only —
      // in prod a failed fetch must surface as an error, never a fabricated persona.
      // (Vite statically strips the dead branch when import.meta.env.DEV is false.)
      try {
        enrichedClient = await clientService.getEnrichedClient(clientId);
        console.log('Loaded client from API:', clientId);
      } catch (apiError) {
        if (!import.meta.env.DEV) throw apiError;

        console.log('API fetch failed, trying seed data (dev only):', apiError);
        const seedClient = getSeedClient(clientId);
        if (seedClient) {
          enrichedClient = seedClient as Client;
          console.log('Loaded client from seed data:', clientId);
        } else {
          throw new Error('Client not found in API or seed data');
        }
      }

      if (!enrichedClient) {
        throw new Error('Client not found');
      }

      // TASK-179: No validation needed - allow viewing client even without risk profile
      // Risk profile can be managed through the Risk Profile tab
      setClient(enrichedClient);

      // Fetch KYC profile for personal/financial data (may not exist yet)
      try {
        const kyc = await kycService.profiles.get(clientId);
        setKycProfile(kyc);
      } catch {
        // KYC profile may not exist yet — that's ok
      }

      // Build timeline from extracted data
      const events = buildTimeline(enrichedClient);
      // TASK-930: the notes API is the ONE source for `communication` events.
      //
      // `buildTimeline` renders `metadata.communications`, and the notes API returns those
      // same entries PLUS `metadata.notes` — so pushing its result alongside the existing
      // branch showed every communication twice, on top of a duplication the API already has
      // of its own (57 rows for 29 distinct items on the live book, pre-existing and not this
      // ticket's to fix). Taking one source and deduping is what makes the timeline agree with
      // the Notes tab instead of multiplying it.
      try {
        const noteResponse = await clientService.getNotes(clientId, { limit: 100 });
        const noteEvents = timelineEventsFromNotes(noteResponse?.notes ?? []);
        if (noteEvents.length) {
          const withoutCommunications = events.filter((e) => e.type !== 'communication');
          events.length = 0;
          events.push(...withoutCommunications, ...dedupeEvents(noteEvents));
        }
        events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      } catch (noteError) {
        // The rest of the timeline is already built and correct, and `metadata.communications`
        // is still in it — a failed notes read must not blank the timeline. The Notes tab
        // reports its own failure.
        console.error('Timeline: notes could not be loaded', noteError);
      }
      setTimelineEvents(events);

      toast.success('Client details loaded');
    } catch (error) {
      console.error('Error fetching client details:', error);
      toast.error('Failed to load client details');
    } finally {
      setLoading(false);
    }
  };

  /**
   * TASK-930: a note is a note — it belongs on the timeline beside the meetings and life
   * events it sits between. `communication` is the existing filter for "the client and the
   * firm exchanged something", which is what a note records.
   */
  /**
   * One entry per thing the client actually said.
   *
   * Not keyed on `id`: the duplicates the notes API emits carry DIFFERENT ids for the same
   * item, so an id-keyed dedupe lets every one of them through. Not keyed on `date|title`
   * either — `date` is day-granularity (it comes from an `<input type="date">`) and `title`
   * falls back to a free-text summary, so two genuinely distinct notes written on one day
   * under a repeated summary ("Call with client") would collapse and one would VANISH from
   * the timeline. Dropping a real note is a worse failure than showing a duplicate.
   *
   * The body is what makes them the same or different: two entries agreeing on the day, the
   * summary AND the full text are the same entry twice.
   */
  const dedupeEvents = (events: TimelineEvent[]): TimelineEvent[] => {
    const seen = new Set<string>();
    return events.filter((e) => {
      const key = `${e.date}|${e.title}|${e.description ?? ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const timelineEventsFromNotes = (notes: Note[]): TimelineEvent[] =>
    notes.map((note) => ({
      id: `note-${note.id}`,
      date: note.date || note.created_at,
      type: 'communication' as const,
      title: note.summary || 'Client note',
      description: note.content ?? note.summary ?? '',
      metadata: note,
      icon: <MessageSquare className="w-4 h-4" />,
      color: 'purple',
    }));

  const buildTimeline = (client: Client): TimelineEvent[] => {
    const events: TimelineEvent[] = [];
    const metadata = client.metadata || {};

    // Add meeting notes
    if (metadata.meeting_notes) {
      metadata.meeting_notes.forEach((note: any, index: number) => {
        events.push({
          id: `meeting-${index}`,
          date: note.date,
          type: 'meeting',
          title: 'Client Meeting',
          description: note.summary,
          metadata: note,
          icon: <Calendar className="w-4 h-4" />,
          color: 'blue'
        });
      });
    }

    // Add life events
    if (metadata.life_events) {
      metadata.life_events.forEach((event: any, index: number) => {
        events.push({
          id: `life-${index}`,
          date: event.date,
          type: 'life_event',
          title: event.type.charAt(0).toUpperCase() + event.type.slice(1),
          description: event.description,
          metadata: event,
          icon: <TrendingUp className="w-4 h-4" />,
          color: event.financial_impact === 'high' ? 'slate' : 'blue'
        });
      });
    }

    // Add communications
    if (metadata.communications) {
      metadata.communications.forEach((comm: any, index: number) => {
        events.push({
          id: `comm-${index}`,
          date: comm.date,
          type: 'communication',
          title: comm.subject,
          description: comm.summary,
          metadata: comm,
          icon: comm.type === 'email' ? <Mail className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />,
          color: 'purple'
        });
      });
    }

    // Sort by date (most recent first)
    return events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  };

  const toggleEventExpansion = (eventId: string) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedEvents(newExpanded);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getEventColorClasses = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'bg-blue-50 text-blue-700 border-blue-200',
      slate: 'bg-slate-50 text-slate-700 border-slate-300',
      sky: 'bg-sky-50 text-sky-700 border-sky-200',
      purple: 'bg-purple-50 text-purple-700 border-purple-200'
    };
    return colors[color] || colors.blue;
  };

  // Shorthand for risk_profile sections (financial snapshot card below)
  const rp = client?.risk_profile;
  const s3 = rp?.section3;

  // Computed financial values from risk_profile section3 (flat camelCase)
  const computedTotalAssets = s3 ? calculateTotalAssets(s3) : 0;
  const computedTotalLiabilities = s3 ? calculateTotalLiabilities(s3) : 0;
  const computedNetWorth = s3 ? calculateNetWorth(s3) : 0;
  const computedLiquidAssets = s3 ? (s3.assetsCashDeposits || 0) + (s3.assetsSecuritiesInvestments || 0) : 0;
  const computedTotalIncome = s3 ? calculateTotalIncome(s3) : 0;

  // Use KYC profile as primary source, risk_profile computations as fallback
  const totalAssets = kycProfile?.total_net_worth || computedTotalAssets;
  const netWorth = kycProfile?.total_net_worth ?? computedNetWorth;
  const liquidAssets = kycProfile?.liquid_net_worth || computedLiquidAssets;
  const annualIncome = kycProfile?.annual_income || computedTotalIncome;
  const totalLiabilities = computedTotalLiabilities;
  
  // TASK-358: Handle task creation for this client
  const handleCreateTask = async (taskData: TaskCreateRequest) => {
    setIsCreatingTask(true);
    try {
      await taskService.createTask(taskData);
      toast.success('Task created successfully');
      setIsTaskModalOpen(false);
    } catch (error: any) {
      console.error('Error creating task:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to create task';
      toast.error(errorMessage);
    } finally {
      setIsCreatingTask(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kielis-navy mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading client details...</p>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <p className="text-gray-600">Client not found</p>
          <button
            onClick={() => navigate('/clients')}
            className="mt-4 px-4 py-2 bg-kielis-navy text-white rounded-md hover:bg-opacity-90"
          >
            Back to Clients
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/clients')}
                className="mr-4 p-2 hover:bg-gray-100 rounded-md"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
                <p className="text-sm text-gray-600">{client.type} • {client.jurisdiction}</p>
              </div>
            </div>
            {/* TASK-358: Add Task button */}
            <button
              onClick={() => setIsTaskModalOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-[#002B45] text-white text-sm font-medium rounded-md hover:bg-[#003d5c] transition-colors"
            >
              <ListTodo className="w-4 h-4 mr-2" />
              Add Task
            </button>
          </div>
        </div>
      </div>

      {/* Client Snapshot Cards - Similar to Phase2 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Financial Snapshot — sourced from KYC profile + risk_profile section3 */}
        {(kycProfile || s3) && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Financial Snapshot</h3>
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
                <p className="text-xs text-gray-600 font-medium">Total Assets</p>
                <p className="text-xl font-bold text-gray-900">
                  CHF {(totalAssets / 1000000).toFixed(1)}M
                </p>
                <p className="text-xs text-blue-600 mt-1">Portfolio value</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
                <p className="text-xs text-gray-600 font-medium">Net Worth</p>
                <p className="text-xl font-bold text-gray-900">
                  CHF {((netWorth - totalLiabilities) / 1000000).toFixed(1)}M
                </p>
                <p className="text-xs text-green-600 mt-1">After liabilities</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-600 font-medium">Liquid Assets</p>
                <p className="text-xl font-bold text-gray-900">
                  CHF {(liquidAssets / 1000000).toFixed(1)}M
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {totalAssets > 0 ? Math.round((liquidAssets / totalAssets) * 100) : 0}% of total
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-600 font-medium">Annual Income</p>
                <p className="text-xl font-bold text-gray-900">
                  CHF {(annualIncome / 1000).toFixed(0)}k
                </p>
                <p className="text-xs text-gray-500 mt-1">Net income</p>
              </div>
            </div>

            {/* Additional Financial Details */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Real Estate</span>
                  <span className="text-sm font-medium">
                    CHF {((s3?.assetsRealEstateMarketValue || 0) / 1000000).toFixed(1)}M
                  </span>
                </div>
                <div className="mt-1 w-full bg-gray-200 rounded-full h-1">
                  <div
                    className="bg-blue-500 h-1 rounded-full"
                    style={{ width: `${totalAssets > 0 ? ((s3?.assetsRealEstateMarketValue || 0) / totalAssets) * 100 : 0}%` }}
                  />
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Business Interests</span>
                  <span className="text-sm font-medium">
                    CHF {((s3?.assetsBusinessInterests || 0) / 1000).toFixed(0)}k
                  </span>
                </div>
                <div className="mt-1 w-full bg-gray-200 rounded-full h-1">
                  <div
                    className="bg-purple-500 h-1 rounded-full"
                    style={{ width: `${totalAssets > 0 ? ((s3?.assetsBusinessInterests || 0) / totalAssets) * 100 : 0}%` }}
                  />
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Debt</span>
                  <span className="text-sm font-medium text-slate-600">
                    CHF {(totalLiabilities / 1000).toFixed(0)}k
                  </span>
                </div>
                <div className="mt-1 w-full bg-gray-200 rounded-full h-1">
                  <div
                    className="bg-slate-500 h-1 rounded-full"
                    style={{ width: `${totalAssets > 0 ? (totalLiabilities / totalAssets) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TASK-604: the snapshot IS the DNA summary — the first screen reads the
            knowledge graph, never contradicting the DNA tab one click away. */}
        {(
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <div className="flex items-start justify-between gap-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {client.name}
                {dnaSummary.mandate && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    Mandate: {dnaSummary.mandate}
                  </span>
                )}
              </h3>
              <button
                type="button"
                onClick={() => setActiveTab('dna')}
                className="shrink-0 text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                Open DNA →
              </button>
            </div>
            {dnaSummary.isEmpty ? (
              <p className="text-sm text-gray-500 italic">
                No client DNA on file yet — it builds from meetings and documents. Open the DNA
                tab to review what's known.
              </p>
            ) : (
              <div className="space-y-2 text-sm">
                {dnaSummary.occupation && (
                  <p className="text-gray-800">
                    {dnaSummary.occupation}
                    {dnaSummary.family && <span className="text-gray-600"> · Family: {dnaSummary.family}</span>}
                  </p>
                )}
                {!dnaSummary.occupation && dnaSummary.family && (
                  <p className="text-gray-800">Family: {dnaSummary.family}</p>
                )}
                <p className="text-gray-700">
                  <span className="text-gray-500">◆ Risk profile:</span>{' '}
                  <span className="font-medium">
                    {riskProfileStatusLabel(client.risk_profile)}
                  </span>
                </p>
                {dnaSummary.focus && (
                  <p className="text-gray-700">
                    <span className="text-gray-500">◆ Focus:</span> {dnaSummary.focus}
                  </p>
                )}
                {dnaSummary.redLines.length > 0 && (
                  <p className="text-red-700">
                    <span aria-hidden>⛔</span> Red line: {dnaSummary.redLines.join(' · ')}
                  </p>
                )}
                {dnaSummary.lifeEventCount > 0 && (
                  <p className="text-amber-700">
                    <span aria-hidden>⚡</span> {dnaSummary.lifeEventCount}{' '}
                    {dnaSummary.lifeEventCount === 1 ? 'life event' : 'life events'} on file
                  </p>
                )}
                {dnaSummary.narrative && (
                  <p className="text-gray-600">{dnaSummary.narrative}</p>
                )}
              </div>
            )}

            {/* AI Behavioral Insights - if available */}
            {client?.metadata?.profile_data?.behavioral_insights && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <Brain className="w-5 h-5 text-purple-600" />
                  <h4 className="text-sm font-medium text-purple-900">AI Behavioral Insights</h4>
                  {client?.metadata?.profile_data?.extraction_metadata?.confidence_scores && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded ml-auto">
                      {Math.round((client.metadata.profile_data.extraction_metadata.confidence_scores.demographics || 0) * 100)}% Confidence
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  {client.metadata.profile_data.behavioral_insights.communication_style && (
                    <div className="flex items-start gap-2">
                      <span className="text-purple-500 mt-0.5">•</span>
                      <p className="text-sm text-gray-700">
                        Communication style: <span className="font-medium">{client.metadata.profile_data.behavioral_insights.communication_style}</span>
                      </p>
                    </div>
                  )}
                  {client.metadata.profile_data.behavioral_insights.decision_pattern && (
                    <div className="flex items-start gap-2">
                      <span className="text-purple-500 mt-0.5">•</span>
                      <p className="text-sm text-gray-700">
                        Decision pattern: <span className="font-medium">{client.metadata.profile_data.behavioral_insights.decision_pattern}</span>
                      </p>
                    </div>
                  )}
                  {client.metadata.profile_data.behavioral_insights.interests && client.metadata.profile_data.behavioral_insights.interests.length > 0 && (
                    <div className="flex items-start gap-2">
                      <span className="text-purple-500 mt-0.5">•</span>
                      <p className="text-sm text-gray-700">
                        Key interests: <span className="font-medium">{client.metadata.profile_data.behavioral_insights.interests.join(', ')}</span>
                      </p>
                    </div>
                  )}
                  {client.metadata.profile_data.behavioral_insights.concerns && client.metadata.profile_data.behavioral_insights.concerns.length > 0 && (
                    <div className="flex items-start gap-2">
                      <span className="text-purple-500 mt-0.5">•</span>
                      <p className="text-sm text-gray-700">
                        Main concerns: <span className="font-medium">{client.metadata.profile_data.behavioral_insights.concerns.join(', ')}</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Client Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <Mail className="w-5 h-5 text-gray-400 mr-3" />
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="text-sm font-medium">{client.email}</p>
              </div>
            </div>
          </div>
          
          {client.phone && (
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center">
                <Phone className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="text-sm font-medium">{client.phone}</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <Building className="w-5 h-5 text-gray-400 mr-3" />
              <div>
                <p className="text-xs text-gray-500">Risk Profile</p>
                <p className="text-sm font-medium">
                  {riskProfileStatusLabel(client.risk_profile)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <FileText className="w-5 h-5 text-gray-400 mr-3" />
              <div>
                <p className="text-xs text-gray-500">Documents</p>
                <p className="text-sm font-medium">{client.total_documents}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('timeline')}
                className={`py-3 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'timeline'
                    ? 'border-kielis-gold text-kielis-navy'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Timeline
              </button>
              {/* TASK-604: DNA + Portfolios promoted — knowledge first, admin tabs after */}
              <button
                onClick={() => setActiveTab('dna')}
                className={`py-3 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'dna'
                    ? 'border-kielis-gold text-kielis-navy'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                DNA
              </button>
              <button
                onClick={() => setActiveTab('portfolios')}
                className={`py-3 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'portfolios'
                    ? 'border-kielis-gold text-kielis-navy'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Portfolios
              </button>
              {/* TASK-229: Unified Compliance tab replaces KYC and Risk Profile */}
              <button
                onClick={() => setActiveTab('compliance')}
                className={`py-3 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'compliance'
                    ? 'border-kielis-gold text-kielis-navy'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Compliance
              </button>
              <button
                onClick={() => setActiveTab('documents')}
                className={`py-3 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'documents'
                    ? 'border-kielis-gold text-kielis-navy'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Documents
              </button>
              <button
                onClick={() => setActiveTab('notes')}
                className={`py-3 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'notes'
                    ? 'border-kielis-gold text-kielis-navy'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Notes
              </button>
              <button
                onClick={() => setActiveTab('insights')}
                className={`py-3 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'insights'
                    ? 'border-kielis-gold text-kielis-navy'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Insights
              </button>
              <button
                onClick={() => setActiveTab('voice')}
                className={`py-3 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'voice'
                    ? 'border-kielis-gold text-kielis-navy'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Voice Note
              </button>
              <button
                onClick={() => setActiveTab('actions')}
                className={`py-3 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'actions'
                    ? 'border-kielis-gold text-kielis-navy'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Action Items
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'timeline' && (
              <div className="space-y-4">
                {timelineEvents.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5" data-testid="timeline-filters">
                    {(
                      [
                        ['all', 'All'],
                        ['meeting', 'Meetings'],
                        ['life_event', 'Life events'],
                        ['communication', 'Communications'],
                        ['document', 'Documents'],
                      ] as const
                    ).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setTimelineFilter(value)}
                        aria-pressed={timelineFilter === value}
                        className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                          timelineFilter === value
                            ? 'border-gray-400 bg-gray-100 text-gray-900'
                            : 'border-gray-200 bg-white text-gray-500 hover:text-gray-800'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
                {timelineEvents.length > 0 ? (
                  groupedTimeline.map((group) => (
                    <div key={group.month} data-testid="timeline-month">
                      <button
                        type="button"
                        onClick={() => toggleMonth(group.month)}
                        aria-expanded={!group.collapsed}
                        className="mb-2 flex w-full items-center gap-2 rounded-md px-1 py-1 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        {group.collapsed ? (
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronUp className="h-4 w-4 text-gray-400" />
                        )}
                        {group.label}
                        <span className="text-xs font-normal text-gray-500">
                          · {group.events.length} record{group.events.length === 1 ? '' : 's'}
                        </span>
                      </button>
                      {!group.collapsed &&
                        group.events.map((event) => (
                    <div key={event.id} className="border-l-2 border-gray-200 pl-4 ml-2">
                      <div className="relative">
                        <div className="absolute -left-6 mt-1.5 w-3 h-3 bg-white border-2 border-gray-400 rounded-full"></div>
                        <div className={`p-4 rounded-lg border ${getEventColorClasses(event.color)}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start">
                              <div className="mr-3 mt-1">{event.icon}</div>
                              <div className="flex-1">
                                <h4 className="font-medium">{event.title}</h4>
                                <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                                <p className="text-xs text-gray-500 mt-2">
                                  <Clock className="inline w-3 h-3 mr-1" />
                                  {formatDate(event.date)}
                                </p>
                                
                                {event.metadata && expandedEvents.has(event.id) && (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    {event.type === 'meeting' && event.metadata.topics && (
                                      <div className="mb-2">
                                        <p className="text-xs font-medium text-gray-700">Topics:</p>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {event.metadata.topics.map((topic: string, idx: number) => (
                                            <span key={idx} className="px-2 py-1 bg-gray-100 text-xs rounded">
                                              {topic}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {event.type === 'meeting' && event.metadata.action_items && event.metadata.action_items.length > 0 && (
                                      <div>
                                        <p className="text-xs font-medium text-gray-700">Action Items:</p>
                                        <ul className="mt-1 space-y-1">
                                          {event.metadata.action_items.map((item: string, idx: number) => (
                                            <li key={idx} className="text-xs text-gray-600">• {item}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    
                                    {event.type === 'life_event' && (
                                      <p className="text-xs">
                                        Financial Impact: <span className="font-medium">{event.metadata.financial_impact}</span>
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {event.metadata && (
                              <button
                                onClick={() => toggleEventExpansion(event.id)}
                                className="ml-2 p-1 hover:bg-gray-100 rounded"
                              >
                                {expandedEvents.has(event.id) ? (
                                  <ChevronUp className="w-4 h-4" />
                                ) : (
                                  <ChevronDown className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                        ))}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">No timeline events yet</p>
                    <p className="text-sm text-gray-500 mt-1">Upload documents to extract client data</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'documents' && (
              <DocumentsTab
                clientId={client.id}
                clientName={client.name}
                clientEmail={client.email}
                // TASK-232: Navigate to compliance tab after extraction approval
                onNavigateToCompliance={() => {
                  console.log('🚀 TASK-232: ClientDetail navigating to Compliance tab');
                  setActiveTab('compliance');
                }}
              />
            )}

            {/* TASK-229: Unified Compliance tab with sub-navigation */}
            {activeTab === 'compliance' && (
              <ComplianceTab
                clientId={client.id}
                clientName={client.name}
                clientEmail={client.email}
                clientPhone={client.phone}
                onNavigateToDocuments={() => setActiveTab('documents')}
              />
            )}

            {activeTab === 'notes' && (
              <NotesTab clientId={client.id} clientName={client.name} />
            )}

            {activeTab === 'insights' && (
              <ClientInsightsTab clientId={client.id} />
            )}

            {activeTab === 'portfolios' && (
              <PortfoliosTab
                clientId={client.id}
                clientName={client.name}
              />
            )}

            {activeTab === 'dna' && <DnaCard clientId={client.id} />}

            {activeTab === 'voice' && (
              <VoiceNoteWidget clientId={client.id} clientName={client.name} />
            )}

            {/* TASK-575: fixed home for the client's meeting action items (also reachable via open_view). */}
            {activeTab === 'actions' && (
              <MeetingActionItemsWidget clientId={client.id} />
            )}
          </div>
        </div>
      </div>

      {/* TASK-358: Create Task Modal */}
      <AddTaskModal
        isOpen={isTaskModalOpen}
        onClose={() => !isCreatingTask && setIsTaskModalOpen(false)}
        onSubmit={handleCreateTask}
        isLoading={isCreatingTask}
        clientId={client.id}
        clientName={client.name}
      />
    </div>
  );
};

export default ClientDetail;